import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Table, Spinner, Alert } from 'react-bootstrap';
import dayjs from 'dayjs';
import { fetchPlanByNatural_key } from '../../../services/salesPlan';

// Define Plan Interface
interface Plan {
  plan_id: number;
  natural_key: string;
  service_id: number;
  plan_name: string;
  pricing: string;
  billing_period: string;
  plan_type: string;
  tier: string;
  max_users: number;
  valid_from: string;
  valid_to: string;
  CDC_FLAG: string;
  created_at: string;
}

// Plan Details Component
const PlanDetails = () => {
  const { planId } = useParams<{ planId: string }>();
  const [plan, setPlan] = useState<Plan | null>(null);
  const [historyPlans, setHistoryPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadPlanDetails = async () => {
      try {
        const planData = await fetchPlanByNatural_key(planId);

        // Find active plan (valid_to = '9999-12-31 23:59:59')
        const activePlan =
          planData.find((p: Plan) => p.valid_to === '9999-12-31 23:59:59') ||
          null;

        // Get history plans (valid_to NOT '9999-12-31 23:59:59')
        const historicalPlans = planData.filter(
          (p: Plan) => p.valid_to !== '9999-12-31 23:59:59'
        );

        setPlan(activePlan);
        setHistoryPlans(historicalPlans);
      } catch (err) {
        setError('Failed to fetch plan details. Please try again.');
        console.error('Error fetching plan:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPlanDetails();
  }, [planId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" />
        <p className="ms-2">Loading Plan Details...</p>
      </div>
    );
  }

  if (!plan) {
    return <Alert variant="danger">Error: Plan not found</Alert>;
  }

  return (
    <div>
      {/* 🔹 Active Plan */}
      <Card className="mb-4 shadow">
        <Card.Body>
          <h4 className="fw-bold">Active Plan</h4>
          <p>
            <strong>Plan Name:</strong> {plan.plan_name}
          </p>
          <p>
            <strong>Pricing:</strong> ₹
            {parseFloat(plan.pricing).toLocaleString()}
          </p>
          <p>
            <strong>Billing Period:</strong> {plan.billing_period}
          </p>
          <p>
            <strong>Plan Type:</strong> {plan.plan_type}
          </p>
          <p>
            <strong>Tier:</strong> {plan.tier}
          </p>
          <p>
            <strong>Max Users:</strong> {plan.max_users}
          </p>
          <small className="text-muted">
            Active From: {dayjs(plan.valid_from).format('DD/MM/YYYY HH:mm:ss')}
          </small>
        </Card.Body>
      </Card>

      {/* 🔹 Plan History */}
      <Card className="mb-4 shadow">
        <Card.Body>
          <h5 className="fw-bold">Plan History</h5>
          {historyPlans.length > 0 ? (
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  <th>Plan ID</th>
                  <th>Plan Name</th>
                  <th>Pricing</th>
                  <th>Billing Period</th>
                  <th>Plan Type</th>
                  <th>Tier</th>
                  <th>Max Users</th>
                  <th>Valid From</th>
                  <th>Valid To</th>
                </tr>
              </thead>
              <tbody>
                {historyPlans.map(p => (
                  <tr key={p.plan_id}>
                    <td>{p.plan_id}</td>
                    <td>{p.plan_name}</td>
                    <td>₹{parseFloat(p.pricing).toLocaleString()}</td>
                    <td>{p.billing_period}</td>
                    <td>{p.plan_type}</td>
                    <td>{p.tier}</td>
                    <td>{p.max_users}</td>
                    <td>{dayjs(p.valid_from).format('DD/MM/YYYY HH:mm:ss')}</td>
                    <td>{dayjs(p.valid_to).format('DD/MM/YYYY HH:mm:ss')}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : (
            <p className="text-muted">No history records found.</p>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default PlanDetails;
